import React, { useState, useEffect, useContext } from 'react';
import './Dashboard.css';
import Url from '../Url';
import { LoginContext } from '../Login/LoginContext';

import {
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ResponsiveContainer,
} from 'recharts';

export default function Dashboard() {
    const { loginResult } = useContext(LoginContext);

    const [selectedMember, setSelectedMember] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('Completed'); // For bar chart filter
    const [dashdata, setDashdata] = useState([]);
    const user_id = loginResult?.user_id;

    useEffect(() => {
        fetch(
            `${Url.Url}/get_report?data=` +
            encodeURIComponent(
                JSON.stringify({
                    report_id: 8,
                    user_id: user_id,
                })
            )
        )
            .then((res) => res.json())
            .then((data) => {
                console.log('Dashboard Data Response:', data[0]);
                if (Array.isArray(data) && Array.isArray(data)) {
                    setDashdata(data);

                    // Set default selected member on first load
                    const firstMember = [...new Set(data.map((item) => item.Name))];
                    setSelectedMember(firstMember || '');
                }
            })
            .catch((err) => console.error('API Error:', err));
    }, [user_id]);

    // Calculate total leads
    const totalLeads = dashdata.length;

    // Count by status (for stats)
    const leadsCountByStatus = dashdata.reduce((acc, lead) => {
        acc[lead.status] = (acc[lead.status] || 0) + 1;
        return acc;
    }, {});

    // Group by member and status
    const teamPieData = dashdata.reduce((acc, lead) => {
        if (!acc[lead.Name]) {
            acc[lead.Name] = {};
        }
        acc[lead.Name][lead.status] = (acc[lead.Name][lead.status] || 0) + 1;
        return acc;
    }, {});

    // Format for PieChart: array of { name, value }
    const formattedTeamPieData = {};
    for (const member in teamPieData) {
        formattedTeamPieData[member] = Object.entries(teamPieData[member]).map(
            ([name, value]) => ({ name, value })
        );
    }

    // Extract all unique statuses for dropdown
    const allStatuses = Array.from(
        new Set(dashdata.map((lead) => lead.status))
    ).sort();

    // Prepare bar chart data filtered by selectedStatus
    const barChartData = Object.entries(teamPieData)
        .map(([name, statuses]) => {
            return {
                name,
                value: statuses[selectedStatus] || 0,
            };
        })
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)
        .map((item, index) => ({
            ...item,
            Rank: index + 1,
        }));

    const COLORS = [
        '#0d47a1', // dark blue
        '#c62828', // dark red
        '#2e7d32', // dark green
        '#6a1b9a', // dark purple
        '#ff6f00', // deep amber
        '#00838f', // dark cyan
        '#4e342e', // deep brown
        '#37474f', // blue grey
        '#3e2723', // chocolate brown
        '#1b5e20', // forest green
        '#1565c0', // vivid blue
        '#ad1457', // raspberry
        '#4527a0', // indigo
        '#00695c', // teal
    ];
          
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div
                    style={{
                        background: '#ffffff',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#333',
                        border: '1px solid #ccc',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                        pointerEvents: 'none',
                    }}
                >
                    <strong>{data.name}</strong>
                    <br />
                    Rank: {data.Rank}
                    <br />
                    {selectedStatus}: {data.value}
                </div>
            );
        }
        return null;
    };

    return (
        <div>
            <div className="dashboard-stats-container">
                <div className="dashboard-stat-box">
                    <div className="dashboard-stat-label">Total Leads</div>
                    <div className="dashboard-stat-value">{totalLeads}</div>
                </div>

                <div className="dashboard-stat-box">
                    <div className="dashboard-stat-label">Total New Enquiries</div>
                    <div className="dashboard-stat-value">{leadsCountByStatus['Enq'] || 0}</div>
                </div>

                <div className="dashboard-stat-box">
                    <div className="dashboard-stat-label">Successfully Done Leads</div>
                    <div className="dashboard-stat-value">{leadsCountByStatus['Completed'] || 0}</div>
                </div>

                <div className="dashboard-stat-box">
                    <div className="dashboard-stat-label">In Progress Leads</div>
                    <div className="dashboard-stat-value">{leadsCountByStatus['Follow-up'] || 0}</div>
                </div>
            </div>

            <div className="dashboard-bottom-section">
                <div className="dashboard-pie-chart-wrapper">
                    <h3 className="dashboard-pie-title">Team Analyzation</h3>
                    <div className="dashboard-dropdown">
                        <select
                            id="member-select"
                            value={selectedMember}
                            onChange={(e) => setSelectedMember(e.target.value)}
                        >
                            {Object.keys(formattedTeamPieData).map((member) => (
                                <option key={member} value={member}>
                                    {member}
                                </option>
                            ))}
                        </select>
                    </div>

                    {formattedTeamPieData[selectedMember] && (
                        <PieChart width={550} height={250}>
                            <Pie
                                data={formattedTeamPieData[selectedMember]}
                                cx="30%"
                                cy="50%"
                                labelLine={true}
                                dataKey="value"
                                label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                            >
                                {formattedTeamPieData[selectedMember].map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    )}

                    <div className="manual-legend">
                        {formattedTeamPieData[selectedMember]?.map((entry, index) => (
                            <div className="legend-item" key={index}>
                                <span
                                    className="legend-color-box"
                                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                ></span>
                                {entry.name}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="dashboard-bar-chart-box">
                    <h3 className="dashboard-pie-title">Top 5 Team Members by Status</h3>

                    {/* Status dropdown for bar chart */}
                    <div className='dashboard-dropdown' style={{ marginBottom: '10px' }}>
    
                        <select
                            id="status-select"
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            {allStatuses.map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={barChartData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                            barCategoryGap={16}
                        >
                            <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                            <YAxis />
                            <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                            <Bar dataKey="value" fill="#66bb6a" barSize={40} radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
